import { EMPTY_STRING } from '../../../constants/common'
import { formatNumberWithSeparators, parseAmount } from '../../../utils/number'

import {
  CURRENCY_SELECT_DECIMAL_SEPARATOR,
  CURRENCY_SELECT_GROUPING_SEPARATOR,
  CURRENCY_SELECT_INTEGER_FORMAT_OPTIONS,
  CURRENCY_SELECT_MAX_FRACTION_DIGITS,
  CURRENCY_SELECT_SIGNIFICANT_CHARACTER,
} from './CurrencySelect.constants'

function isSignificantChar(character) {
  return CURRENCY_SELECT_SIGNIFICANT_CHARACTER.test(character)
}

function stripLeadingZeros(digits) {
  return digits.replace(/^0+(?=\d)/, EMPTY_STRING)
}

/**
 * Split a typed German-format amount into its value parts. `.` is treated as a
 * (cosmetic) thousands separator and dropped; `,` is the decimal separator.
 */
function parseTypedAmount(typed) {
  const cleaned = String(typed ?? EMPTY_STRING).replace(/[^\d,]/g, EMPTY_STRING)
  const decimalIndex = cleaned.indexOf(CURRENCY_SELECT_DECIMAL_SEPARATOR)

  if (decimalIndex === -1) {
    return {
      fractionDigits: EMPTY_STRING,
      hasDecimal: false,
      integerDigits: stripLeadingZeros(cleaned),
    }
  }

  return {
    fractionDigits: cleaned
      .slice(decimalIndex + 1)
      .replace(/\D/g, EMPTY_STRING)
      .slice(0, CURRENCY_SELECT_MAX_FRACTION_DIGITS),
    hasDecimal: true,
    integerDigits: stripLeadingZeros(cleaned.slice(0, decimalIndex)),
  }
}

function groupIntegerDigits(integerDigits, hasDecimal) {
  if (integerDigits) {
    return formatNumberWithSeparators(
      integerDigits,
      CURRENCY_SELECT_INTEGER_FORMAT_OPTIONS
    )
  }

  // No integer digits yet — show a leading 0 only once decimals are started.
  return hasDecimal ? '0' : EMPTY_STRING
}

/** Build the German display string (`2.000.000,50`) from value parts. */
function formatParts({ fractionDigits, hasDecimal, integerDigits }) {
  const groupedInteger = groupIntegerDigits(integerDigits, hasDecimal)

  return hasDecimal
    ? `${groupedInteger}${CURRENCY_SELECT_DECIMAL_SEPARATOR}${fractionDigits}`
    : groupedInteger
}

/**
 * Build the canonical amount string for storage (`.` decimal, no grouping).
 * Empty input → '' ; a trailing decimal separator with no digits → integer only.
 */
function partsToRaw({ fractionDigits, hasDecimal, integerDigits }) {
  if (!integerDigits && !fractionDigits) {
    return EMPTY_STRING
  }

  const integer = integerDigits || (hasDecimal ? '0' : EMPTY_STRING)

  return fractionDigits ? `${integer}.${fractionDigits}` : integer
}

function countSignificant(value) {
  return Array.from(value).filter(isSignificantChar).length
}

/**
 * Reconstruct the single contiguous edit between two input values via their
 * common prefix/suffix: what the user inserted and what got removed.
 */
function getEditDiff(previousValue, value) {
  const maxAffixLength = Math.min(previousValue.length, value.length)

  let prefixLength = 0

  while (
    prefixLength < maxAffixLength &&
    previousValue[prefixLength] === value[prefixLength]
  ) {
    prefixLength += 1
  }

  let suffixLength = 0

  while (
    suffixLength < maxAffixLength - prefixLength &&
    previousValue[previousValue.length - 1 - suffixLength] ===
      value[value.length - 1 - suffixLength]
  ) {
    suffixLength += 1
  }

  return {
    inserted: value.slice(prefixLength, value.length - suffixLength),
    removed: previousValue.slice(
      prefixLength,
      previousValue.length - suffixLength
    ),
  }
}

/** Canonical raw (`50000.00`) of an already-masked display string. */
function getMaskedDisplayRaw(display, commaIndex) {
  const integer = stripLeadingZeros(
    display.slice(0, commaIndex).replace(/\D/g, EMPTY_STRING)
  )
  const fraction = display.slice(commaIndex + 1).replace(/\D/g, EMPTY_STRING)

  return `${integer || '0'}.${fraction}`
}

/**
 * Force the fraction to exactly `fractionDigits` slots. Overflow (a digit typed
 * into a full fraction) overwrites the digit at the caret; underflow (a deleted
 * or replaced digit) is zero-padded, so the two decimals are always present.
 */
function normalizeFractionDigits({
  fractionCaretOffset,
  fractionDigits,
  fractionRaw,
}) {
  if (fractionRaw.length === fractionDigits) {
    return fractionRaw
  }

  if (fractionRaw.length > fractionDigits) {
    const removeIndex = Math.min(
      Math.max(fractionCaretOffset, 0),
      fractionRaw.length - 1
    )

    return (
      fractionRaw.slice(0, removeIndex) + fractionRaw.slice(removeIndex + 1)
    ).slice(0, fractionDigits)
  }

  // Underflow (a deleted/replaced digit): restore the emptied slot with a zero
  // at the caret so the other decimals keep their position.
  const insertIndex = Math.min(
    Math.max(fractionCaretOffset, 0),
    fractionRaw.length
  )
  const zeros = '0'.repeat(fractionDigits - fractionRaw.length)

  return (
    fractionRaw.slice(0, insertIndex) +
    zeros +
    fractionRaw.slice(insertIndex)
  ).slice(0, fractionDigits)
}

function getCaretBySignificantCount(formatted, significantCount) {
  if (significantCount <= 0) {
    return 0
  }

  let seen = 0

  for (let index = 0; index < formatted.length; index += 1) {
    if (isSignificantChar(formatted[index])) {
      seen += 1

      if (seen === significantCount) {
        return index + 1
      }
    }
  }

  return formatted.length
}

/**
 * Convert a stored amount (`2000000.00`, `1234.5`, '') into its display string.
 * With `minimumFractionDigits: 0` insignificant trailing zeros are dropped so a
 * whole amount reads as `2.000.000`, while real decimals are kept (`1.234,5`).
 * Pass `2` to force a fixed decimal precision such as `50.000,00`.
 */
export function rawToMoneyDisplay(rawAmount, minimumFractionDigits = 0) {
  if (rawAmount == null || rawAmount === EMPTY_STRING) {
    return EMPTY_STRING
  }

  const numberValue = parseAmount(rawAmount)

  if (Number.isNaN(numberValue)) {
    return EMPTY_STRING
  }

  return formatNumberWithSeparators(numberValue, {
    maximumFractionDigits: CURRENCY_SELECT_MAX_FRACTION_DIGITS,
    minimumFractionDigits,
  })
}

/**
 * Handle a keystroke in the amount input: returns the reformatted display
 * string, the canonical raw value to emit, and the caret position to restore.
 */
export function getMoneyInputChange({ selectionStart, value }) {
  const significantBeforeCaret = countSignificant(
    value.slice(0, selectionStart)
  )
  const parts = parseTypedAmount(value)
  const display = formatParts(parts)

  return {
    caret: getCaretBySignificantCount(display, significantBeforeCaret),
    display,
    raw: partsToRaw(parts),
  }
}

/**
 * Keystroke handler for the fixed-precision money mask: the comma and its
 * `fractionDigits` decimals are always shown (`50.000,00`). The comma is a
 * protected part of the mask — typing one jumps the caret into the cents, and
 * a partial edit that would swallow it (merging the cents into the integer) is
 * rejected instead of silently multiplying the amount.
 */
export function getFixedFractionMoneyInputChange({
  fractionDigits,
  previousValue = EMPTY_STRING,
  selectionStart,
  value,
}) {
  const previousCommaIndex = previousValue.indexOf(
    CURRENCY_SELECT_DECIMAL_SEPARATOR
  )
  const { inserted, removed } = getEditDiff(previousValue, value)

  if (
    previousCommaIndex !== -1 &&
    inserted === CURRENCY_SELECT_DECIMAL_SEPARATOR &&
    !removed
  ) {
    return {
      caret:
        selectionStart - 1 <= previousCommaIndex
          ? previousCommaIndex + 1
          : selectionStart - 1,
      display: previousValue,
      raw: getMaskedDisplayRaw(previousValue, previousCommaIndex),
    }
  }

  const commaIndex = value.indexOf(CURRENCY_SELECT_DECIMAL_SEPARATOR)
  const hasComma = commaIndex !== -1

  if (!hasComma && previousCommaIndex !== -1) {
    if (removed === CURRENCY_SELECT_DECIMAL_SEPARATOR && !inserted) {
      return {
        caret: previousCommaIndex,
        display: previousValue,
        raw: getMaskedDisplayRaw(previousValue, previousCommaIndex),
      }
    }

    if (
      inserted &&
      removed !== previousValue &&
      value.includes(CURRENCY_SELECT_GROUPING_SEPARATOR)
    ) {
      return {
        caret: previousCommaIndex,
        display: previousValue,
        raw: getMaskedDisplayRaw(previousValue, previousCommaIndex),
      }
    }
  }

  const integerDigits = stripLeadingZeros(
    (hasComma ? value.slice(0, commaIndex) : value).replace(/\D/g, EMPTY_STRING)
  )
  const fractionRaw = (
    hasComma ? value.slice(commaIndex + 1) : EMPTY_STRING
  ).replace(/\D/g, EMPTY_STRING)

  const hasSignificantFraction = /[1-9]/.test(fractionRaw)

  if (!integerDigits && !hasSignificantFraction) {
    return { caret: 0, display: EMPTY_STRING, raw: EMPTY_STRING }
  }

  const fractionCaretOffset = hasComma ? selectionStart - commaIndex - 1 : 0

  const fraction = normalizeFractionDigits({
    fractionCaretOffset,
    fractionDigits,
    fractionRaw,
  })

  const groupedInteger = groupIntegerDigits(integerDigits, true)
  const display = `${groupedInteger}${CURRENCY_SELECT_DECIMAL_SEPARATOR}${fraction}`

  const caretInInteger = !hasComma || selectionStart <= commaIndex
  const caret = caretInInteger
    ? getCaretBySignificantCount(
        groupedInteger,
        countSignificant(value.slice(0, selectionStart))
      )
    : groupedInteger.length +
      CURRENCY_SELECT_DECIMAL_SEPARATOR.length +
      Math.min(Math.max(fractionCaretOffset, 0), fractionDigits)

  return {
    caret,
    display,
    raw: `${integerDigits || '0'}.${fraction}`,
  }
}
